// src/taskpane/taskpane.js
// Entry point — imports all modules, configures marked, wires Office.onReady.

import { marked } from "marked";
import {
  syncButtonStates,
  toggleSuggestEdits,
  pendingDeltaCount,
  computePendingDelta,
  updateSyncStatus,
  autoSyncEnabled,
  documentReady,
  documentId,
  chatHistoryOffset,
  setLastScan,
  setChatMode,
  setDarkTheme,
  isDarkTheme,
  loadFromSettings,
  jurisdiction,
  setJurisdiction as setJurisdictionState,
} from "../modules/state";
import { tryRestoreSession, initializeDocument, flushToBackend } from "../modules/init";
import { handleGetSelection, handleAsk, getSessionMessages, getSessionMessageCount, _formatDateLabel } from "../modules/chat";
import { toggleDebugLogs, setUserDisplayName, _labels, renderMarkdown, _cleanAIResponse } from "../modules/messages";
import { getChatHistory } from "../core/apiClient";
import {
  checkAuth,
  handleLogin,
  handleTwoFA,
  handleLogout,
  toggleBackupMode,
  showLoginScreen,
  getCurrentUser,
  refreshUserProfile,
} from "../modules/auth";
import { scanDocument, applyAndDiffToActive } from "../core/documentScanner";
import { locateByText } from "../modules/selection";

// Configure marked for safe, compact output
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Debounced handler for Word paragraph events (user edits)
let _editDebounce = null;
async function _onWordChanged() {
  clearTimeout(_editDebounce);
  _editDebounce = setTimeout(async () => {
    if (!documentReady || !documentId) return;
    try {
      const scan = await scanDocument({ skipPageNumbers: true });
      applyAndDiffToActive(scan.nodes);
      setLastScan(scan);
      const pending = pendingDeltaCount();
      updateSyncStatus(pending > 0 ? "pending" : "synced", pending);
    } catch (e) {
      // Scan errors during live editing are non-fatal
    }
  }, 2000);
}

Office.onReady(async (info) => {
  if (info.host === Office.HostType.Word) {
    document.getElementById("sideload-msg").style.display = "none";
    // Note: app-body visibility is controlled by auth, not shown here

    // ── Detect Office dark mode from theme colors ──────────────────────────
    function _detectOfficeDark() {
      try {
        const theme = Office.context.officeTheme;
        if (!theme || !theme.bodyBackgroundColor) return false;
        const bg = theme.bodyBackgroundColor.replace("#", "");
        const r = parseInt(bg.substr(0, 2), 16);
        const g = parseInt(bg.substr(2, 2), 16);
        const b = parseInt(bg.substr(4, 2), 16);
        return (r * 299 + g * 587 + b * 114) / 1000 < 128;
      } catch { return false; }
    }

    // Helper: apply theme and sync the toggle UI
    function _applyThemeUI(dark) {
      setDarkTheme(dark);
      document.getElementById("theme-toggle").classList.toggle("on", dark);
      const sun = document.getElementById("theme-icon-sun");
      const moon = document.getElementById("theme-icon-moon");
      if (sun) sun.style.display = dark ? "none" : "";
      if (moon) moon.style.display = dark ? "" : "none";
    }

    // ── Restore / auto-detect theme ────────────────────────────────────────
    const userSetTheme = loadFromSettings("miai_theme_user_set");
    if (userSetTheme) {
      // User has explicitly chosen a theme — always respect it
      _applyThemeUI(!!loadFromSettings("miai_dark_theme"));
    } else {
      // No explicit preference — follow Word's current theme
      _applyThemeUI(_detectOfficeDark());
    }

    // ── Existing document interaction buttons ──────────────────────────────
    document.getElementById("get-selection-btn").addEventListener("click", handleGetSelection);
    document.getElementById("ask-btn").addEventListener("click", handleAsk);
    document.getElementById("suggest-edits-toggle").addEventListener("click", toggleSuggestEdits);

    document.getElementById("user-input").addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAsk();
      }
    });
    document.getElementById("user-input").addEventListener("input", syncButtonStates);
    syncButtonStates();

    // ── Debug toggle ──────────────────────────────────────────────────────
    // document.getElementById("debug-toggle").addEventListener("click", toggleDebugLogs);

    // ── Mode bar buttons ──────────────────────────────────────────────────
    document.querySelectorAll(".mode-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const mode = btn.dataset.mode;
        const isActive = btn.classList.contains("active");
        document.querySelectorAll(".mode-btn").forEach((b) => b.classList.remove("active"));
        if (!isActive) {
          btn.classList.add("active");
          setChatMode(mode);
        } else {
          setChatMode(null);
        }
      });
    });

    // ── Profile icon → navigate to settings view ─────────────────────────
    document.getElementById("profile-btn").addEventListener("click", () => {
      document.getElementById("main-view").style.display = "none";
      document.getElementById("settings-view").style.display = "flex";
    });

    // ── Settings back → navigate to main view ────────────────────────────
    document.getElementById("settings-back").addEventListener("click", () => {
      document.getElementById("settings-view").style.display = "none";
      document.getElementById("main-view").style.display = "flex";
    });

    // ── Dark mode toggle ──────────────────────────────────────────────────
    document.getElementById("theme-toggle").addEventListener("click", () => {
      setDarkTheme(!isDarkTheme, true); // persist=true: user explicitly set preference
      document.getElementById("theme-toggle").classList.toggle("on", isDarkTheme);
      const sun = document.getElementById("theme-icon-sun");
      const moon = document.getElementById("theme-icon-moon");
      if (sun) sun.style.display = isDarkTheme ? "none" : "";
      if (moon) moon.style.display = isDarkTheme ? "" : "none";
    });

    // ── Jurisdiction dropdown (in Account settings) ───────────────────────
    const JURISDICTIONS = ["CTH", "NSW", "QLD", "VIC", "TAS", "ACT", "WA", "SA", "NT"];

    const jurisdictionDropdown = document.getElementById("settings-jurisdiction-dropdown");
    const jurisdictionPill = document.getElementById("settings-jurisdiction-pill");

    function setJurisdiction(jur) {
      setJurisdictionState(jur);  // sync to state so chat API picks it up
      const labelEl = document.getElementById("settings-jurisdiction");
      if (labelEl) labelEl.textContent = jur;
      jurisdictionDropdown.querySelectorAll(".jurisdiction-option").forEach((b) => {
        b.classList.toggle("active", b.textContent === jur);
      });
    }

    // Restore previously saved jurisdiction
    const savedJurisdiction = loadFromSettings("miai_jurisdiction");
    if (savedJurisdiction && JURISDICTIONS.includes(savedJurisdiction)) {
      setJurisdiction(savedJurisdiction);
    }

    JURISDICTIONS.forEach((jur) => {
      const btn = document.createElement("button");
      btn.className = "jurisdiction-option" + (jur === jurisdiction ? " active" : "");
      btn.textContent = jur;
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        setJurisdiction(jur);
        jurisdictionDropdown.style.display = "none";
      });
      jurisdictionDropdown.appendChild(btn);
    });

    function openSettingsJurisdictionDropdown() {
      const pillRect = jurisdictionPill.getBoundingClientRect();
      // Show briefly off-screen to measure height before final positioning
      jurisdictionDropdown.style.visibility = "hidden";
      jurisdictionDropdown.style.top = "0px";
      jurisdictionDropdown.style.display = "block";
      const dropH = jurisdictionDropdown.offsetHeight || 200;
      jurisdictionDropdown.style.visibility = "";
      const spaceBelow = window.innerHeight - pillRect.bottom;
      const top = spaceBelow >= dropH + 8 ? pillRect.bottom + 4 : pillRect.top - dropH - 4;
      jurisdictionDropdown.style.top = `${Math.max(4, top)}px`;
      jurisdictionDropdown.style.right = `${window.innerWidth - pillRect.right}px`;
    }

    jurisdictionPill.addEventListener("click", (e) => {
      e.stopPropagation();
      if (jurisdictionDropdown.style.display === "none") {
        openSettingsJurisdictionDropdown();
      } else {
        jurisdictionDropdown.style.display = "none";
      }
    });

    document.addEventListener("click", (e) => {
      const wrap = document.getElementById("settings-jurisdiction-wrap");
      if (wrap && !wrap.contains(e.target)) {
        jurisdictionDropdown.style.display = "none";
      }
    });

    // ── Search ────────────────────────────────────────────────────────────
    const _srchHeader      = document.querySelector(".ms-welcome__header");
    const _srchInput       = document.getElementById("search-input");
    const _srchResults     = document.getElementById("search-results");
    const _srchChatArea    = document.getElementById("chat-area");
    const _srchModeBar     = document.getElementById("mode-bar");
    const _srchContextBox  = document.getElementById("context-box");
    const _srchInputArea   = document.querySelector(".input-area");

    // Parse UTC timestamps (backend sends ISO without Z)
    function _srchParseTs(ts) {
      if (!ts) return null;
      if (typeof ts === "string" && !ts.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(ts)) ts += "Z";
      return new Date(ts);
    }

    // Extra messages fetched during this search session (discarded on close)
    let _srchExtraCache = [];
    let _srchExtraOffset = 0;
    let _srchExtraHasMore = true;
    let _srchFetching = false;
    let _srchDebounce = null;

    function _srchAllMessages() {
      // Session messages (persisted) + extra fetched during search
      return [...getSessionMessages(), ..._srchExtraCache];
    }

    function _activateSearch() {
      _srchHeader.classList.add("search-active");
      _srchChatArea.style.display = "none";
      _srchModeBar.style.display = "none";
      _srchContextBox.style.display = "none";
      _srchInputArea.style.display = "none";
      _srchResults.style.display = "flex";
      // Reset extra cache — start from where session messages left off
      _srchExtraCache = [];
      _srchExtraOffset = chatHistoryOffset;
      _srchExtraHasMore = true;
      _srchFetching = false;
      _srchInput.focus();
      _srchRenderHint();
    }

    function _deactivateSearch() {
      _srchHeader.classList.remove("search-active");
      _srchChatArea.style.display = "";
      _srchModeBar.style.display = "";
      _srchContextBox.style.display = "";
      _srchInputArea.style.display = "";
      _srchResults.style.display = "none";
      _srchInput.value = "";
      clearTimeout(_srchDebounce);
      // Discard extra search cache; session messages stay
      _srchExtraCache = [];
    }

    function _srchRenderHint() {
      _srchResults.innerHTML = `<div class="search-hint">Type at least 3 characters to search</div>`;
    }

    function _srchAppendLoadMoreBtn() {
      const btn = document.createElement("button");
      btn.className = "search-load-more-btn";
      btn.innerHTML =
        `<svg width="13" height="13" viewBox="0 0 16 16" fill="none">` +
        `<path d="M8 3v10M4 9l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>` +
        `</svg>Search in older messages`;
      btn.addEventListener("click", _srchFetchMore);
      _srchResults.appendChild(btn);
    }

    async function _srchFetchMore() {
      if (_srchFetching || !_srchExtraHasMore) return;
      _srchFetching = true;
      const btn = _srchResults.querySelector(".search-load-more-btn");
      if (btn) {
        btn.disabled = true;
        btn.innerHTML =
          `<svg class="spin" width="13" height="13" viewBox="0 0 16 16" fill="none">` +
          `<path d="M8 2a6 6 0 1 1-4.24 1.76" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` +
          `</svg>Searching…`;
      }
      try {
        const data = await getChatHistory(documentId, 15, _srchExtraOffset);
        const msgs = data.messages || [];
        // Deduplicate against session messages
        for (const msg of msgs) {
          const dup = _srchAllMessages().some(
            (m) => m.timestamp === msg.timestamp && m.role === msg.role
          );
          if (!dup) _srchExtraCache.push(msg);
        }
        _srchExtraOffset += msgs.length;
        _srchExtraHasMore = data.has_more || false;
        // Re-run search with expanded corpus, preserving scroll position
        const q = _srchInput.value.trim();
        if (q.length >= 3) {
          _srchRun(q, true);
        } else {
          _srchRenderHint();
        }
      } catch {
        if (btn) { btn.disabled = false; btn.textContent = "Retry"; }
      } finally {
        _srchFetching = false;
      }
    }

    // Walk text nodes inside an element and wrap query matches with <mark>
    function _srchHighlight(el, query) {
      if (!query) return;
      const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escaped})`, "gi");

      function walk(node) {
        if (node.nodeType === Node.TEXT_NODE) {
          if (!node.textContent.toLowerCase().includes(query.toLowerCase())) return;
          const frag = document.createDocumentFragment();
          let last = 0;
          let m;
          regex.lastIndex = 0;
          while ((m = regex.exec(node.textContent)) !== null) {
            if (m.index > last) frag.appendChild(document.createTextNode(node.textContent.slice(last, m.index)));
            const mark = document.createElement("mark");
            mark.className = "srch-highlight";
            mark.textContent = m[0];
            frag.appendChild(mark);
            last = m.index + m[0].length;
          }
          if (last < node.textContent.length) frag.appendChild(document.createTextNode(node.textContent.slice(last)));
          node.parentNode.replaceChild(frag, node);
        } else if (node.nodeType === Node.ELEMENT_NODE && node.nodeName !== "MARK") {
          Array.from(node.childNodes).forEach(walk);
        }
      }
      Array.from(el.childNodes).forEach(walk);
    }

    // Extract clean display content + selection ref text from a raw message object
    function _srchExtractContent(msg) {
      let content = msg.content || "";
      let refText = msg.selection_text || null;
      if (!refText) {
        const m = content.match(
          /^\[Selected text: "([\s\S]*?)"\](?:\n\[Node IDs: [\s\S]*?\])?\n\n([\s\S]*)$/
        );
        if (m) { refText = m[1]; content = m[2]; }
      }
      return { content, refText };
    }

    function _srchBuildResultItem(msg, query) {
      const isAI = msg.role === "assistant";
      const { content, refText } = _srchExtractContent(msg);

      const item = document.createElement("div");
      item.className = "search-result-item" + (isAI ? " ai" : " user");

      // Header: role + timestamp
      const hdr = document.createElement("div");
      hdr.className = "search-result-header";
      const role = document.createElement("span");
      role.className = "search-result-role" + (isAI ? " ai" : "");
      role.textContent = isAI ? "JEWEL" : (_labels.user || "You");
      const time = document.createElement("span");
      time.className = "search-result-time";
      const d = _srchParseTs(msg.timestamp);
      time.textContent = d
        ? d.toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
        : "";
      hdr.appendChild(role);
      hdr.appendChild(time);
      item.appendChild(hdr);

      // Selection pill — clickable; silently does nothing if text no longer exists in doc
      if (refText && refText.trim()) {
        const pill = document.createElement("div");
        pill.className = "msg-ctx-pill";
        const span = document.createElement("span");
        span.className = "ctx-pill-label";
        const rt = refText.trim();
        span.textContent = "@" + (rt.length > 40 ? rt.slice(0, 40) + "…" : rt);
        pill.appendChild(span);
        const captured = rt;
        pill.addEventListener("click", () => locateByText(captured));
        item.appendChild(pill);
      }

      const body = document.createElement("div");
      body.className = "search-result-body";
      if (isAI) {
        body.innerHTML = renderMarkdown(_cleanAIResponse(content));
      } else {
        body.textContent = content;
      }
      if (query) _srchHighlight(body, query);
      item.appendChild(body);
      return item;
    }

    function _srchRun(query, preserveScroll = false) {
      const q = query.toLowerCase();
      const all = _srchAllMessages();
      const matches = all.filter((msg) => {
        // Match only against the question/response text — not selected context
        const { content } = _srchExtractContent(msg);
        return content.toLowerCase().includes(q);
      });
      // Latest first
      matches.sort((a, b) => {
        if (a.timestamp && b.timestamp) return _srchParseTs(b.timestamp) - _srchParseTs(a.timestamp);
        return 0;
      });

      const prevScrollTop = preserveScroll ? _srchResults.scrollTop : 0;
      const prevScrollHeight = preserveScroll ? _srchResults.scrollHeight : 0;

      _srchResults.innerHTML = "";

      if (matches.length === 0) {
        _srchResults.innerHTML = `<div class="search-no-results">No results for "<b>${q}</b>"</div>`;
        if (_srchExtraHasMore) _srchAppendLoadMoreBtn();
      } else {
        let lastDateLabel = null;
        matches.forEach((msg) => {
          const dateLabel = _formatDateLabel(msg.timestamp);
          if (dateLabel !== lastDateLabel) {
            lastDateLabel = dateLabel;
            const sep = document.createElement("div");
            sep.className = "date-separator";
            sep.innerHTML =
              `<div class="date-sep-line"></div>` +
              `<span class="date-sep-label">${dateLabel}</span>` +
              `<div class="date-sep-line"></div>`;
            _srchResults.appendChild(sep);
          }
          _srchResults.appendChild(_srchBuildResultItem(msg, q));
        });
        if (_srchExtraHasMore) _srchAppendLoadMoreBtn();
      }

      // Restore scroll position relative to content growth (keeps user at same visual spot)
      if (preserveScroll) {
        const newScrollHeight = _srchResults.scrollHeight;
        _srchResults.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
      }
    }

    document.getElementById("search-btn").addEventListener("click", _activateSearch);
    document.getElementById("search-close-btn").addEventListener("click", _deactivateSearch);

    _srchInput.addEventListener("input", () => {
      clearTimeout(_srchDebounce);
      const q = _srchInput.value.trim();
      if (q.length < 3) { _srchRenderHint(); return; }
      _srchDebounce = setTimeout(() => _srchRun(q), 300);
    });

    // ── Settings sign out ─────────────────────────────────────────────────
    document.getElementById("settings-signout").addEventListener("click", handleLogout);

    // ── Password eye toggle ───────────────────────────────────────────────
    document.getElementById("auth-pw-toggle").addEventListener("click", () => {
      const pwInput = document.getElementById("auth-password");
      const showIcon = document.getElementById("auth-pw-icon-show");
      const hideIcon = document.getElementById("auth-pw-icon-hide");
      const isPassword = pwInput.type === "password";
      pwInput.type = isPassword ? "text" : "password";
      showIcon.style.display = isPassword ? "none" : "";
      hideIcon.style.display = isPassword ? "" : "none";
    });

    // ── Intercept all links → open in default browser ─────────────────────
    function _openExternalLink(e) {
      const anchor = e.target.closest("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href === "#" || href.startsWith("javascript:")) return;
      e.preventDefault();
      e.stopPropagation();
      try {
        Office.context.ui.openBrowserWindow(href);
      } catch {
        window.open(href, "_blank");
      }
    }
    document.getElementById("chat-area").addEventListener("click", _openExternalLink);
    document.getElementById("search-results").addEventListener("click", _openExternalLink);

    // ── Auth UI ────────────────────────────────────────────────────────────
    document.getElementById("signin-btn").addEventListener("click", handleLogin);
    document.getElementById("auth-password").addEventListener("keydown", (e) => {
      if (e.key === "Enter") handleLogin();
    });
    document.getElementById("auth-email").addEventListener("keydown", (e) => {
      if (e.key === "Enter") document.getElementById("auth-password")?.focus();
    });

    document.getElementById("twofa-submit-btn").addEventListener("click", handleTwoFA);
    // Auto-submit TOTP when 6 digits entered
    document.getElementById("twofa-code-input").addEventListener("input", (e) => {
      const digits = e.target.value.replace(/\D/g, "");
      if (digits.length === 6) handleTwoFA();
    });
    // Backup code: auto-insert hyphen after 4 chars, auto-submit when complete
    document.getElementById("twofa-backup-input").addEventListener("input", (e) => {
      const input = e.target;
      const raw = input.value.replace(/-/g, "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      const capped = raw.slice(0, 8);
      const formatted = capped.length > 4 ? capped.slice(0, 4) + "-" + capped.slice(4) : capped;
      input.value = formatted;
      // Always keep cursor at end
      input.setSelectionRange(formatted.length, formatted.length);
      if (capped.length === 8) handleTwoFA();
    });

    document.getElementById("twofa-mode-toggle").addEventListener("click", toggleBackupMode);
    document.getElementById("back-to-login").addEventListener("click", showLoginScreen);

    // ── AutoSave banner dismiss ────────────────────────────────────────────
    document.getElementById("autosave-dismiss").addEventListener("click", () => {
      document.getElementById("autosave-banner").style.display = "none";
    });

    // ── Auto-sync every 60s ────────────────────────────────────────────────
    setInterval(() => {
      if (autoSyncEnabled && pendingDeltaCount() > 0) flushToBackend();
    }, 60_000);

    // ── Auth events (register BEFORE checkAuth so dispatch is caught) ──────
    window.addEventListener("miai:authenticated", async () => {
      // Refresh user profile from auth API to get latest org/plan data
      let user = getCurrentUser();
      try {
        const fresh = await refreshUserProfile();
        if (fresh) user = fresh;
      } catch {}

      // Populate settings view with user info
      if (user) {
        const name = user.full_name || user.name || user.email || "";
        const email = user.email || "";
        const org = user.organisation || user.organization || user.org || user.company || user.company_name || user.firm || "";
        const plan = user.subscription_plan || user.plan || user.plan_name || user.plan_type || user.subscription_status || "";
        const initials = name
          .split(" ")
          .map((w) => w[0])
          .slice(0, 2)
          .join("")
          .toUpperCase() || "?";

        const avatarEl = document.getElementById("settings-avatar");
        const nameEl   = document.getElementById("settings-name");
        const emailEl  = document.getElementById("settings-email");
        if (avatarEl) avatarEl.textContent = initials;
        if (nameEl)   nameEl.textContent   = name;
        if (emailEl)  emailEl.textContent  = email;

        setUserDisplayName(name);

        // Account info rows
        const sinfoName  = document.getElementById("sinfo-name");
        const sinfoEmail = document.getElementById("sinfo-email");
        const sinfoOrg   = document.getElementById("sinfo-org");
        const sinfoPlan  = document.getElementById("sinfo-plan");
        if (sinfoName)  sinfoName.textContent  = name  || "—";
        if (sinfoEmail) sinfoEmail.textContent = email || "—";
        if (sinfoOrg)   sinfoOrg.textContent   = org   || "—";
        if (sinfoPlan)  sinfoPlan.textContent  = plan  || "—";
      }

      const restored = await tryRestoreSession();
      if (!restored) await initializeDocument();

      // Register Word paragraph change listeners + onSaved after auth
      try {
        Word.run(async (context) => {
          context.document.onParagraphAdded.add(_onWordChanged);
          context.document.onParagraphChanged.add(_onWordChanged);
          context.document.onParagraphDeleted.add(_onWordChanged);

          // Hook 1: flush on every Word save (Ctrl+S, AutoSave, File→Save)
          // Requires WordApi 1.5 + onSaved to be present — skipped silently otherwise
          try {
            if (
              Office.context.requirements.isSetSupported("WordApi", "1.5") &&
              context.document.onSaved
            ) {
              context.document.onSaved.add(() => flushToBackend());
            }
          } catch {
            // onSaved not available on this host — skip silently
          }

          await context.sync();
        });
      } catch (e) {
        // Paragraph/save events not supported in this host version — silently skip
      }
    });

    // Hook 2: keepalive fetch when task pane iframe is torn down (close/refresh/crash recovery)
    window.addEventListener("beforeunload", () => {
      const { added, removed } = computePendingDelta();
      if (!added.length && !removed.length) return;
      if (!documentId) return;

      const BASE_URL = process.env.BACKEND_URL || "";
      const token = (() => {
        try {
          return JSON.parse(localStorage.getItem("miai_auth_token"))?.access_token || null;
        } catch {
          return null;
        }
      })();

      const url = `${BASE_URL}/document/${encodeURIComponent(documentId)}/delta`;
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ added, removed, updated: [] }),
        keepalive: true, // survives page unload
      });
      // fire-and-forget — no await (unload context)
    });

    // Hook 3: opportunistic flush when user switches away from Word
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden" && documentReady && pendingDeltaCount() > 0) {
        flushToBackend();
      }
    });

    window.addEventListener("miai:auth-expired", handleLogout);

    // ── Check auth — shows login screen or fires miai:authenticated ────────
    await checkAuth();
  }
});
